import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Button, 
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert
} from '@mui/material';
import { 
  Search as SearchIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import { tipoutApi, DateSummary, TipOutLog } from '../services/tipoutService.ts';
import { employeeApi, Employee } from '../services/employeeService.ts';
import printService, { PrintData } from '../services/printService.ts';

const TipOutLogForms: React.FC = () => {
  const { sessionToken } = useAuth();
  const { showError } = useSnackbar();

  // State for table data and filters
  const [summaryData, setSummaryData] = useState<DateSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState<string>('');
  const [searchDate, setSearchDate] = useState<string>('');

  // Per-date AM/PM totals (Bar 1%, Bus 1.5%, Expo, Net Sales), keyed by date.
  // Fetched separately since the summary endpoint only returns the date itself.
  interface DateTotals {
    amNetSales: number;
    pmNetSales: number;
    bar: number;
    bus: number;
    expo: number;
    totalTipOut: number;
    totalNetSales: number;
  }
  const [dateTotals, setDateTotals] = useState<Record<string, DateTotals>>({});
  
  // Ref to prevent duplicate API calls
  const hasLoadedRef = useRef(false);

  // Fetch summary data from API
  const fetchSummaryData = async (date?: string) => {
    if (!sessionToken) return;
    
    try {
      setLoading(true);
      console.log('Fetching summary data...', { date, hasLoaded: hasLoadedRef.current });
      const response = await tipoutApi.getDateSummary(sessionToken, {
        date: date,
        page: 1,
        limit: 100
      });
      setSummaryData(response.data);
      hasLoadedRef.current = true;
      fetchDateTotals(response.data.map(row => row.date));
    } catch (error) {
      console.error('Error fetching summary data:', error);
      showError('Failed to fetch tip-out summary data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch AM/PM (daily) logs for every listed date and roll each one up into the
  // AM/PM Net Sales, Bar 1%, Bus 1.5%, Expo, and total columns. Dates with no logs
  // simply default to $0.00 via the fallback in the row renderer below.
  const fetchDateTotals = async (dates: string[]) => {
    if (!sessionToken || dates.length === 0) return;

    try {
      const results = await Promise.all(
        dates.map(date => tipoutApi.getDateLogs(sessionToken, date))
      );

      const totalsByDate: Record<string, DateTotals> = {};
      dates.forEach((date, idx) => {
        const logs: TipOutLog[] = results[idx] || [];
        const dailyLogs = logs.filter(log => log.eventType === 'daily');
        const amLogs = dailyLogs.filter(log => log.shift === 'AM');
        const pmLogs = dailyLogs.filter(log => log.shift === 'PM');

        const amNetSales = amLogs.reduce((sum, log) => sum + (Number(log.netSales) || 0), 0);
        const pmNetSales = pmLogs.reduce((sum, log) => sum + (Number(log.netSales) || 0), 0);
        const bar = dailyLogs.reduce((sum, log) => sum + (Number(log.bar) || 0), 0);
        const bus = dailyLogs.reduce((sum, log) => sum + (Number(log.bus) || 0), 0);
        const expo = dailyLogs.reduce((sum, log) => sum + (Number(log.expo) || 0), 0);
        const totalTipOut = dailyLogs.reduce((sum, log) => sum + (Number(log.totalTipOut) || 0), 0);

        totalsByDate[date] = {
          amNetSales,
          pmNetSales,
          bar,
          bus,
          expo,
          totalTipOut,
          totalNetSales: amNetSales + pmNetSales,
        };
      });

      setDateTotals(totalsByDate);
    } catch (error) {
      console.error('Error fetching date totals:', error);
    }
  };

  // Handle date search
  const handleDateSearch = () => {
    fetchSummaryData(searchDate || undefined);
  };

  // Handle clear filter
  const handleClearFilter = () => {
    setSearchDate('');
    fetchSummaryData();
  };

  // Load data on component mount
  useEffect(() => {
    if (sessionToken && !hasLoadedRef.current) {
      console.log('Initial load triggered');
      fetchSummaryData();
    }
    
    // Cleanup function to reset ref when component unmounts
    return () => {
      hasLoadedRef.current = false;
    };
  }, [sessionToken]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Handle View Report button click
  const handleViewReport = async (date: string) => {
    if (!sessionToken) {
      showError('No session token available');
      return;
    }

    try {
      setLoading(true);
      const onlyDate = (date || '').slice(0, 10);
      
      // Fetch tip-out data for the specific date
      const logs = await tipoutApi.getDateLogs(sessionToken, onlyDate);
      // Fetch employees to resolve names and employee_id
      const employeesResp = await employeeApi.getEmployees({ page: 1, limit: 1000 }, sessionToken);
      const employeeMap = new Map<number, Employee>(employeesResp.data.map(e => [e.id, e]));
      
      // Convert API data to print format
      const printData: PrintData = {
        date: onlyDate,
        daily: {
          am: [],
          pm: []
        },
        event: {
          event1: [],
          event2: []
        }
      };

      // Group logs by section and shift
      logs.forEach(log => {
        const emp = employeeMap.get(log.employeeId);
        const entry = {
          id: '',
          employeeName: emp ? emp.name : `Employee ${log.employeeId}`,
          employeeCode: emp?.employeeId ?? '0',
          position: log.type.charAt(0).toUpperCase() + log.type.slice(1) as 'Server' | 'Host' | 'Bar',
          netSales: log.netSales,
          bar1Percent: log.bar,
          bus1_5Percent: log.bus,
          expoAmount: log.expo,
          totalTipOut: log.totalTipOut,
          section: log.eventType.charAt(0).toUpperCase() + log.eventType.slice(1) as 'Daily' | 'Event',
          shift: log.shift,
        };

        if (log.eventType === 'daily') {
          if (log.shift === 'AM') {
            printData.daily.am.push(entry);
          } else if (log.shift === 'PM') {
            printData.daily.pm.push(entry);
          }
        } else if (log.eventType === 'event') {
          if (log.shift === 'Event1') {
            printData.event.event1.push(entry);
          } else if (log.shift === 'Event2') {
            printData.event.event2.push(entry);
          }
        }
      });

      // Open print dialog
      printService.openPrintDialog(printData);
      
    } catch (error) {
      console.error('Error generating report:', error);
      showError('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Typography variant="h6" sx={{ color: '#7f0000', fontWeight: 700, flex: '0 0 auto', whiteSpace: 'nowrap' }}>
          Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
            Tip Out Log Forms
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and manage tip-out log entries by date
          </Typography>
        </Box>
      </Box>

      {/* Search Filter */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Search & Filter
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Search by Date"
            type="date"
            value={searchDate}
            onChange={(e) => setSearchDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleDateSearch}
            disabled={loading}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            onClick={handleClearFilter}
            disabled={loading}
          >
            Clear
          </Button>
        </Box>
      </Paper>

      {/* Summary Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', border: '2px solid #7f0000' }}>
        <TableContainer>
          <Table sx={{ borderCollapse: 'collapse', '& td': { border: '2px solid #9e9e9e', py: 1.25, px: 1.5 } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#b71c1c' }}>
                {['Date', 'AM Net Sales', 'PM Net Sales', 'Bar 1%', 'Bus 1.5%', 'Expo $3/$4 F/S', 'Total Tip Out', 'Total Net Sales', 'Report'].map((label) => (
                  <TableCell
                    key={label}
                    align={label === 'Date' ? 'left' : label === 'Report' ? 'center' : 'right'}
                    sx={{
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      border: '2px solid #7f0000',
                      py: 1.5,
                      px: 1.5,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Loading tip-out data...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : summaryData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No tip-out data found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                summaryData.map((row) => {
                  const totals = dateTotals[row.date];
                  return (
                    <TableRow key={row.date} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {formatDate(row.date)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">${(totals?.amNetSales ?? 0).toFixed(2)}</TableCell>
                      <TableCell align="right">${(totals?.pmNetSales ?? 0).toFixed(2)}</TableCell>
                      <TableCell align="right">${(totals?.bar ?? 0).toFixed(2)}</TableCell>
                      <TableCell align="right">${(totals?.bus ?? 0).toFixed(2)}</TableCell>
                      <TableCell align="right">${(totals?.expo ?? 0).toFixed(2)}</TableCell>
                      <TableCell align="right">${(totals?.totalTipOut ?? 0).toFixed(2)}</TableCell>
                      <TableCell align="right">${(totals?.totalNetSales ?? 0).toFixed(2)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label="View Report"
                          color="primary"
                          size="small"
                          variant="outlined"
                          onClick={() => handleViewReport(row.date)}
                          sx={{ cursor: 'pointer' }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default TipOutLogForms;
